import { useMemo } from 'react';

import { CategoryFilterFields, FilterConfigField } from '@components/table';
import { ColumnIcon } from '@components/table/column-icon';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { useLists } from 'api/lists/hooks';
import { useActiveUsers } from 'api/users/hooks';

import { makeAttributeId } from '../journeys/traveller-journey-list/columns';

type Configs = Record<'traveller' | 'journey', CategoryFilterFields>;

export const useAttributesFilterConfig = (
  allowedAttributes: Array<AttributeType | TravellerAttributeTemplate>,
): { configs: Configs; isLoading: boolean } => {
  const listsQuery = useLists('?include_hidden=1');
  const usersQuery = useActiveUsers();

  const configs = useMemo(() => {
    let cfgs: Configs = {
      journey: [],
      traveller: [],
    };

    allowedAttributes.forEach((attribute) => {
      let cfg: Partial<FilterConfigField> = {
        id: makeAttributeId(attribute.shortId, Boolean(attribute.isTravellerAttribute)),
        icon: <ColumnIcon type={attribute.attributeType} />,
        label: attribute.title,
        type: attribute.attributeType,
      };

      if (cfg.type === 'multi_select' || cfg.type === 'single_select') {
        const listOptions = attribute.options
          ? listsQuery.data?.find((list) => list.shortId === attribute.options)?.options || []
          : [];
        cfg.values = listOptions.map((opt) => ({ value: opt.shortId, label: opt.label }));
      } else if (cfg.type === 'user') {
        cfg.values =
          usersQuery.data?.map((user) => ({
            label: user.name,
            value: user.shortId,
          })) ?? [];
      }

      if (attribute.isTravellerAttribute) {
        cfgs.traveller.push(cfg as CategoryFilterFields[number]);
      } else {
        cfgs.journey.push(cfg as CategoryFilterFields[number]);
      }
    });

    return cfgs;
  }, [allowedAttributes, listsQuery.data, usersQuery.data]);

  return { isLoading: usersQuery.isLoading || listsQuery.isLoading, configs };
};
